import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  InteractionStatus,
  InteractionType,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { combineLatest, filter, map, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'stena-user-menu',
  imports: [MatIconModule, MatMenuModule, MatButtonModule, MatDividerModule],
  templateUrl: './stena-user-menu.component.html',
  standalone: true,
})
export class StenaUserMenuComponent implements OnInit, OnDestroy {
  private readonly msalGuardConfig =
    inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private readonly authService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);

  @ViewChild(MatMenuTrigger) trigger!: MatMenuTrigger;

  @Input() displayName?: string;
  @Input() displayUsername?: string;
  @Input() menuIcon?: string;

  @Output() handleLoginSuccess: EventEmitter<AccountInfo> = new EventEmitter();

  @Output() handleLogout: EventEmitter<void> = new EventEmitter();

  private account: AccountInfo | null = null;

  private _closeMenu = false;

  @Input()
  set closeMenu(value: boolean | undefined) {
    const nextValue = !!value;
    const changedToTrue = nextValue && !this._closeMenu;
    this._closeMenu = nextValue;

    if (changedToTrue && this.trigger?.menuOpen) {
      this.trigger.closeMenu();
    }
  }

  get closeMenu(): boolean {
    return this._closeMenu;
  }

  get resolvedName() {
    return (
      this.displayName ??
      this.account?.name ??
      (this.account?.idTokenClaims as { name?: string } | undefined)?.name ??
      this.displayUsername ??
      this.account?.username ??
      ''
    );
  }

  get resolvedUsername() {
    return this.displayUsername ?? this.account?.username ?? '';
  }

  isIframe = false;
  private readonly _destroying$ = new Subject<void>();

  isLoggedIn$ = combineLatest([
    this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      takeUntil(this._destroying$)
    ),
    this.authService.handleRedirectObservable(),
  ]).pipe(map(() => this.authService.instance.getAllAccounts().length > 0));

  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener;

    this.isLoggedIn$.subscribe(() => {
      const accounts = this.authService.instance.getAllAccounts() ?? [];
      const isLoggedIn = accounts.length > 0;
      if (!isLoggedIn) {
        this.authService.loginRedirect();
      } else {
        const activeAccount =
          this.authService.instance.getActiveAccount() ?? accounts[0];
        this.authService.instance.setActiveAccount(activeAccount);
        this.account = activeAccount;
        this.handleLoginSuccess.emit(activeAccount);
      }
    });
  }

  login() {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.authService
          .loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
          });
      } else {
        this.authService
          .loginPopup()
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
          });
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        this.authService.loginRedirect({
          ...this.msalGuardConfig.authRequest,
        } as RedirectRequest);
      } else {
        this.authService.loginRedirect();
      }
    }
  }

  logout() {
    this.handleLogout.emit();
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        postLogoutRedirectUri: '/',
        mainWindowRedirectUri: '/',
      });
    } else {
      this.authService.logoutRedirect({
        postLogoutRedirectUri: '/',
      });
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
